import { useEffect, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';

const ORBIT_SMOOTH_SPEED = 10;

const HORIZONTAL_PRESETS = [
  { value: 0, label: 'Front' },
  { value: 45, label: 'Front-right quarter' },
  { value: 90, label: 'Right' },
  { value: 135, label: 'Back-right quarter' },
  { value: 180, label: 'Back' },
  { value: 225, label: 'Back-left quarter' },
  { value: 270, label: 'Left' },
  { value: 315, label: 'Front-left quarter' },
];

const VERTICAL_PRESETS = [
  { value: -30, label: 'Low-angle' },
  { value: 0, label: 'Eye-level' },
  { value: 30, label: 'Elevated' },
  { value: 60, label: 'High-angle' },
];

const DISTANCE_PRESETS = [
  { value: 0.6, label: 'Close-up' },
  { value: 1.0, label: 'Medium' },
  { value: 1.8, label: 'Wide' },
];

const VISUAL_RADIUS_MIN = 0.85;
const VISUAL_RADIUS_MAX = 4.2;
const VISUAL_RADIUS_WHEEL_STEP = 0.1;

const sliderShellClass =
  'group flex items-center gap-2 rounded-full bg-white h-[36px] px-3 py-1.5 text-primary-300 transition-colors hover:text-white hover:ring-white/20';

function presetToVisualRadius(presetValue) {
  if (presetValue <= 0.6) return 1.35;
  if (presetValue <= 1.0) return 1.95;
  return 2.75;
}

function findNearestPresetIndex(value, presets, { circular = false } = {}) {
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < presets.length; i++) {
    const raw = Math.abs(presets[i].value - value);
    const dist = circular ? Math.min(raw, 360 - raw) : raw;
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  }
  return bestIdx;
}

function discreteFillPct(index, count) {
  if (count <= 1) return 100;
  return (index / (count - 1)) * 100;
}

function lerpAngleDeg(from, to, t) {
  const diff = ((to - from + 180) % 360) - 180;
  return from + diff * t;
}

function lerpScalar(from, to, t) {
  return from + (to - from) * t;
}

function dampFactor(delta, speed = ORBIT_SMOOTH_SPEED) {
  return 1 - Math.exp(-speed * delta);
}

function createOrbitStateFromIndices(horizontalIndex, verticalIndex, distanceIndex) {
  const yawDeg = HORIZONTAL_PRESETS[horizontalIndex].value;
  const pitchDeg = VERTICAL_PRESETS[verticalIndex].value;
  const presetRadius = DISTANCE_PRESETS[distanceIndex].value;
  const visualRadius = presetToVisualRadius(presetRadius);
  return {
    yawDeg,
    pitchDeg,
    radius: visualRadius,
    presetRadius,
    targetYawDeg: yawDeg,
    targetPitchDeg: pitchDeg,
    targetVisualRadius: visualRadius,
  };
}

const SmoothedOrbitDriver = ({ orbitRef }) => {
  useFrame((_, delta) => {
    const o = orbitRef.current;
    const t = dampFactor(delta);
    o.yawDeg = lerpAngleDeg(o.yawDeg, o.targetYawDeg, t);
    o.pitchDeg = lerpScalar(o.pitchDeg, o.targetPitchDeg, t);
    o.radius = lerpScalar(o.radius, o.targetVisualRadius, t);
  });
  return null;
};

const StaticCenterBox = () => {
  const boxRef = useRef(null);

  useFrame(() => {
    if (!boxRef.current) return;
    boxRef.current.position.set(0, 0, 0);
    boxRef.current.rotation.set(0, 0, 0);
  });

  return (
    <mesh ref={boxRef} castShadow receiveShadow>
      <boxGeometry args={[1.2, 1.2, 1.2]} />
      <meshBasicMaterial color="#9bb4ff" />
    </mesh>
  );
};

const CameraFigure = ({ orbitRef }) => {
  const markerRef = useRef(null);
  const lineGeometryRef = useRef(null);

  useFrame(() => {
    const { yawDeg, pitchDeg, radius } = orbitRef.current;
    const orbitYaw = (yawDeg * Math.PI) / 180;
    const orbitPitch = (pitchDeg * Math.PI) / 180;
    const mx = radius * Math.cos(orbitPitch) * Math.sin(orbitYaw);
    const my = radius * Math.sin(orbitPitch);
    const mz = radius * Math.cos(orbitPitch) * Math.cos(orbitYaw);

    if (markerRef.current) {
      markerRef.current.position.set(mx, my, mz);
      markerRef.current.lookAt(0, 0, 0);
    }

    if (lineGeometryRef.current) {
      const positions = lineGeometryRef.current.attributes.position.array;
      positions[0] = mx;
      positions[1] = my;
      positions[2] = mz;
      positions[3] = 0;
      positions[4] = 0;
      positions[5] = 0;
      lineGeometryRef.current.attributes.position.needsUpdate = true;
    }
  });

  return (
    <group>
      <line>
        <bufferGeometry ref={lineGeometryRef}>
          <bufferAttribute
            attach="attributes-position"
            args={[new Float32Array([0, 0, 0, 0, 0, 0]), 3]}
          />
        </bufferGeometry>
        <lineBasicMaterial color="#94a3b8" transparent opacity={0.45} />
      </line>

      <group ref={markerRef}>
        <group rotation={[0, -Math.PI / 2, 0]}>
          <mesh>
            <boxGeometry args={[0.28, 0.19, 0.19]} />
            <meshStandardMaterial color="#111827" />
          </mesh>
          <mesh position={[0.19, 0, 0]}>
            <cylinderGeometry args={[0.055, 0.055, 0.13, 18]} />
            <meshStandardMaterial color="#4b5563" />
          </mesh>
          <mesh position={[0, 0.13, 0]}>
            <boxGeometry args={[0.13, 0.065, 0.13]} />
            <meshStandardMaterial color="#1f2937" />
          </mesh>
          <mesh rotation={[0, 0, -Math.PI / 2]} position={[0.35, 0, 0]}>
            <coneGeometry args={[0.048, 0.16, 12]} />
            <meshStandardMaterial color="#22c55e" />
          </mesh>
        </group>
      </group>
    </group>
  );
};

const AutoFrameCamera = ({ orbitRef }) => {
  const { camera, size } = useThree();

  useFrame((_, delta) => {
    const { pitchDeg, radius } = orbitRef.current;
    const orbitPitch = (pitchDeg * Math.PI) / 180;
    const fovRad = (camera.fov * Math.PI) / 180;
    const aspect = size.width > 0 && size.height > 0 ? size.width / size.height : 1;

    const halfBoxExtent = 0.6;
    const figurePad = 0.45;
    const xExtent = radius + figurePad;
    const yExtent = Math.max(
      halfBoxExtent,
      Math.abs(radius * Math.sin(orbitPitch)) + figurePad,
    );

    const zForHeight = yExtent / Math.tan(fovRad / 2);
    const zForWidth = xExtent / (Math.tan(fovRad / 2) * Math.max(aspect, 0.0001));
    const targetZ = Math.max(3.2, zForHeight, zForWidth) + 0.35;

    const frameT = dampFactor(delta, 8);
    camera.position.z = lerpScalar(camera.position.z, targetZ, frameT);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
  });

  return null;
};

/**
 * 3D orbit preview + camera sliders (shared by Transform Models & Render).
 * @param {{
 *   variant?: 'transform' | 'render',
 *   cameraAngle?: { azimuth: number, elevation: number, distance: number },
 *   onCameraChange?: (params: { azimuth: number, elevation: number, distance: number }) => void,
 * }} props
 */
const CameraOrbitControls = ({ variant = 'transform', cameraAngle, onCameraChange }) => {
  const initialIndices = {
    horizontal: findNearestPresetIndex(cameraAngle?.azimuth ?? 0, HORIZONTAL_PRESETS, {
      circular: true,
    }),
    vertical: findNearestPresetIndex(cameraAngle?.elevation ?? 0, VERTICAL_PRESETS),
    distance: findNearestPresetIndex(cameraAngle?.distance ?? 1, DISTANCE_PRESETS),
  };
  const [horizontalIndex, setHorizontalIndex] = useState(initialIndices.horizontal);
  const [verticalIndex, setVerticalIndex] = useState(initialIndices.vertical);
  const [distanceIndex, setDistanceIndex] = useState(initialIndices.distance);

  const orbitRef = useRef(
    createOrbitStateFromIndices(
      initialIndices.horizontal,
      initialIndices.vertical,
      initialIndices.distance,
    ),
  );
  const lastAppliedCameraAngleRef = useRef(
    cameraAngle
      ? {
          azimuth: cameraAngle.azimuth,
          elevation: cameraAngle.elevation,
          distance: cameraAngle.distance,
        }
      : null,
  );
  const dragStateRef = useRef({
    dragging: false,
    pointerId: null,
    lastX: 0,
    lastY: 0,
  });

  const horizontalPreset = HORIZONTAL_PRESETS[horizontalIndex];
  const verticalPreset = VERTICAL_PRESETS[verticalIndex];
  const distancePreset = DISTANCE_PRESETS[distanceIndex];
  const yawFillPct = discreteFillPct(horizontalIndex, HORIZONTAL_PRESETS.length);
  const pitchFillPct = discreteFillPct(verticalIndex, VERTICAL_PRESETS.length);
  const radiusFillPct = discreteFillPct(distanceIndex, DISTANCE_PRESETS.length);

  const applyCameraAngle = (angle) => {
    if (!angle) return;

    const nextHorizontal = findNearestPresetIndex(angle.azimuth, HORIZONTAL_PRESETS, {
      circular: true,
    });
    const nextVertical = findNearestPresetIndex(angle.elevation, VERTICAL_PRESETS);
    const nextDistance = findNearestPresetIndex(angle.distance, DISTANCE_PRESETS);
    const nextPresetRadius = DISTANCE_PRESETS[nextDistance].value;
    const nextVisualRadius = presetToVisualRadius(nextPresetRadius);
    const nextYawDeg = HORIZONTAL_PRESETS[nextHorizontal].value;
    const nextPitchDeg = VERTICAL_PRESETS[nextVertical].value;

    setHorizontalIndex(nextHorizontal);
    setVerticalIndex(nextVertical);
    setDistanceIndex(nextDistance);

    const o = orbitRef.current;
    o.targetYawDeg = nextYawDeg;
    o.targetPitchDeg = nextPitchDeg;
    o.presetRadius = nextPresetRadius;
    o.targetVisualRadius = nextVisualRadius;
    o.yawDeg = nextYawDeg;
    o.pitchDeg = nextPitchDeg;
    o.radius = nextVisualRadius;
  };

  useEffect(() => {
    if (!cameraAngle) return;

    const prev = lastAppliedCameraAngleRef.current;
    if (
      prev &&
      prev.azimuth === cameraAngle.azimuth &&
      prev.elevation === cameraAngle.elevation &&
      prev.distance === cameraAngle.distance
    ) {
      return;
    }

    lastAppliedCameraAngleRef.current = {
      azimuth: cameraAngle.azimuth,
      elevation: cameraAngle.elevation,
      distance: cameraAngle.distance,
    };
    applyCameraAngle(cameraAngle);
  }, [cameraAngle?.azimuth, cameraAngle?.elevation, cameraAngle?.distance]);

  useEffect(() => {
    const o = orbitRef.current;
    o.targetYawDeg = HORIZONTAL_PRESETS[horizontalIndex].value;
    o.targetPitchDeg = VERTICAL_PRESETS[verticalIndex].value;
  }, [horizontalIndex, verticalIndex]);

  useEffect(() => {
    const o = orbitRef.current;
    o.presetRadius = DISTANCE_PRESETS[distanceIndex].value;
    o.targetVisualRadius = presetToVisualRadius(o.presetRadius);
  }, [distanceIndex]);

  const onCameraChangeRef = useRef(onCameraChange);
  onCameraChangeRef.current = onCameraChange;

  useEffect(() => {
    onCameraChangeRef.current?.({
      azimuth: horizontalPreset.value,
      elevation: verticalPreset.value,
      distance: distancePreset.value,
    });
  }, [horizontalPreset.value, verticalPreset.value, distancePreset.value]);

  const beginDrag = (event) => {
    dragStateRef.current.dragging = true;
    dragStateRef.current.pointerId = event.pointerId;
    dragStateRef.current.lastX = event.clientX;
    dragStateRef.current.lastY = event.clientY;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const updateDrag = (event) => {
    if (!dragStateRef.current.dragging) return;
    if (
      dragStateRef.current.pointerId != null &&
      event.pointerId !== dragStateRef.current.pointerId
    ) {
      return;
    }
    const dx = event.clientX - dragStateRef.current.lastX;
    const dy = event.clientY - dragStateRef.current.lastY;
    dragStateRef.current.lastX = event.clientX;
    dragStateRef.current.lastY = event.clientY;

    const o = orbitRef.current;
    o.targetYawDeg = ((o.targetYawDeg - dx * 0.35) % 360 + 360) % 360;
    o.targetPitchDeg = Math.max(-30, Math.min(60, o.targetPitchDeg - dy * 0.18));
  };

  const endDrag = () => {
    dragStateRef.current.dragging = false;
    dragStateRef.current.pointerId = null;

    const o = orbitRef.current;
    const nextHorizontal = findNearestPresetIndex(o.targetYawDeg, HORIZONTAL_PRESETS, {
      circular: true,
    });
    const nextVertical = findNearestPresetIndex(o.targetPitchDeg, VERTICAL_PRESETS);
    setHorizontalIndex(nextHorizontal);
    setVerticalIndex(nextVertical);
    o.targetYawDeg = HORIZONTAL_PRESETS[nextHorizontal].value;
    o.targetPitchDeg = VERTICAL_PRESETS[nextVertical].value;
  };

  const handleWheel = (event) => {
    event.preventDefault();
    const delta = event.deltaY;
    const o = orbitRef.current;
    const step = delta > 0 ? VISUAL_RADIUS_WHEEL_STEP : -VISUAL_RADIUS_WHEEL_STEP;
    o.targetVisualRadius = Math.min(
      VISUAL_RADIUS_MAX,
      Math.max(VISUAL_RADIUS_MIN, o.targetVisualRadius + step),
    );
  };

  const isRenderVariant = variant === 'render';

  return (
    <>
      {isRenderVariant ? (
        <span className="text-center text-[11px] font-semibold tracking-wide text-gray-500">
          Camera angle control
        </span>
      ) : null}

      <div>
        <div
          className="flex h-[180px] touch-none select-none items-center justify-center overflow-hidden rounded-3xl border border-gray-200 bg-white/70 p-2"
          onPointerDown={beginDrag}
          onPointerMove={updateDrag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onPointerLeave={endDrag}
          onWheel={handleWheel}
          title="Drag to orbit camera figure; scroll to adjust 3D distance (slider value unchanged)"
        >
          <Canvas camera={{ position: [0, 0, 8], fov: 50 }} shadows>
            <SmoothedOrbitDriver orbitRef={orbitRef} />
            <AutoFrameCamera orbitRef={orbitRef} />
            <ambientLight intensity={0.8} />
            <StaticCenterBox />
            <CameraFigure orbitRef={orbitRef} />
            <gridHelper args={[10, 10, '#d6d9de', '#e6e9ee']} position={[0, -1.1, 0]} />
          </Canvas>
        </div>
        {!isRenderVariant ? (
          <span className="mt-1 block text-center text-sm font-semibold text-gray-700">
            3D Camera Control
          </span>
        ) : null}
      </div>

      <div
        className={`flex flex-col gap-2.5 px-0.5 pb-1 ${
          isRenderVariant ? 'shrink-0' : 'min-h-0 flex-1'
        }`}
      >
        <div className="flex flex-col gap-1">
          <span className="text-center text-[11px] font-semibold tracking-wide text-gray-500">
            {isRenderVariant ? 'Azimuth (horizontal rotation)' : 'Horizontal'}
          </span>
          <div className={sliderShellClass} title="Orbit angle around the box (yaw)">
            <input
              type="range"
              min={0}
              max={HORIZONTAL_PRESETS.length - 1}
              step={1}
              value={horizontalIndex}
              onChange={(e) => setHorizontalIndex(Number(e.target.value))}
              className="brush-size-slider min-w-0 w-full shrink-0"
              style={{ '--brush-fill': `${yawFillPct}%` }}
              aria-label="Camera figure horizontal angle"
              aria-valuetext={`${horizontalPreset.label} (${horizontalPreset.value}°)`}
            />
          </div>
          <span className="text-center text-[10px] text-gray-600">
            {horizontalPreset.label} ({horizontalPreset.value}°)
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-center text-[11px] font-semibold tracking-wide text-gray-500">
            {isRenderVariant ? 'Elevation (vertical angle)' : 'Vertical'}
          </span>
          <div className={sliderShellClass} title="Height angle above / below horizon (pitch)">
            <input
              type="range"
              min={0}
              max={VERTICAL_PRESETS.length - 1}
              step={1}
              value={verticalIndex}
              onChange={(e) => setVerticalIndex(Number(e.target.value))}
              className="brush-size-slider min-w-0 w-full shrink-0"
              style={{ '--brush-fill': `${pitchFillPct}%` }}
              aria-label="Camera figure vertical angle"
              aria-valuetext={`${verticalPreset.label} (${verticalPreset.value}°)`}
            />
          </div>
          <span className="text-center text-[10px] text-gray-600">
            {verticalPreset.label} ({verticalPreset.value}°)
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-center text-[11px] font-semibold tracking-wide text-gray-500">
            Distance
          </span>
          <div className={sliderShellClass} title="Distance of camera figure from the box">
            <input
              type="range"
              min={0}
              max={DISTANCE_PRESETS.length - 1}
              step={1}
              value={distanceIndex}
              onChange={(e) => setDistanceIndex(Number(e.target.value))}
              className="brush-size-slider min-w-0 w-full shrink-0"
              style={{ '--brush-fill': `${radiusFillPct}%` }}
              aria-label="Camera figure zoom distance"
              aria-valuetext={`${distancePreset.label} (${distancePreset.value})`}
            />
          </div>
          <span className="text-center text-[10px] text-gray-600">
            {distancePreset.label} ({distancePreset.value})
          </span>
        </div>
      </div>
    </>
  );
};

export default CameraOrbitControls;
